// Supabase 认证客户端
// 用于管理员登录认证

import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    '⚠️ Supabase 环境变量未配置。\n' +
    '请在 Cloudflare Pages 设置中配置：\n' +
    '- VITE_SUPABASE_URL\n' +
    '- VITE_SUPABASE_ANON_KEY'
  )
}

export const supabaseAuth = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    flowType: 'pkce', // 使用 PKCE 流程
  },
})

// 认证辅助函数
export const authHelpers = {
  // 登录
  async signIn(email: string, password: string) {
    const { data, error } = await supabaseAuth.auth.signInWithPassword({
      email,
      password,
    })
    if (error) throw error
    return data
  },

  // 注册（仅邮箱）- 需要同时创建 admin 记录
  async signUp(email: string, password: string, name: string) {
    // 1. 在 Supabase Auth 中创建用户
    const { data: authData, error: authError } = await supabaseAuth.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
        data: {
          name,
          user_type: 'admin', // 标记为管理员
        },
      },
    })

    if (authError) throw authError

    // 2. 在 t_admin_users 表中创建记录
    // 注意：这需要通过 API 路由完成，因为客户端没有权限直接写入
    if (authData.user) {
      const response = await fetch('/api/admin/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auth_user_id: authData.user.id,
          email,
          name,
        }),
      })

      if (!response.ok) {
        // 如果创建 admin 记录失败，应该清理 auth 用户
        // 但这需要 service_role 权限，暂时只抛出错误
        throw new Error('创建管理员记录失败')
      }
    }

    return authData
  },

  // 登出
  async signOut() {
    const { error } = await supabaseAuth.auth.signOut()
    if (error) throw error
  },

  // 获取当前会话
  async getSession() {
    const { data, error } = await supabaseAuth.auth.getSession()
    if (error) throw error
    return data.session
  },

  // 获取当前用户
  async getUser() {
    const { data, error } = await supabaseAuth.auth.getUser()
    if (error) throw error
    return data.user
  },

  // 监听认证状态变化
  onAuthStateChange(callback: (event: string, session: any) => void) {
    return supabaseAuth.auth.onAuthStateChange(callback)
  },
}
